import { AuthContext } from '@/contexts/AuthContext';
import { useContext, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import Logo from '../UI/Logo';
import styles from './Navbar.module.css';
import MenuCloseSVG from '@Assets/svg/menuClosed.svg';
import MenuOpenSVG from '@Assets/svg/menuOpen.svg';
import ConfirmationDialog from '@/components/UI/ConfirmationDialog';
import { ReactComponent as NavArrow } from '@Assets/svg/nav_link.svg';
import { ReactComponent as ReportSVG } from '@Assets/svg/report.svg';
import { ReactComponent as ItalianSVG } from '@Assets/svg/it.svg';
import { ReactComponent as EnglishSVG } from '@Assets/svg/en.svg';
import i18n from '@/translations/i18n';
import VideoModal from '../VideoModal';

interface NavbarProps {
	className?: string;
}

const Navbar = (props: NavbarProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const authCtx = useContext(AuthContext);
	const navRef = useRef<HTMLDivElement>(null);

	const isRoleAdmin = authCtx.role?.some((r) => r.name === 'ROLE_ADMIN');
	const [currentLanguage, setCurrentLanguage] = useState<string>('it');

	const [menuOpen, setMenuOpen] = useState<boolean>(false);
	const [showPopup, setShowPopup] = useState<boolean>(false);

	const [showVideoModal, setShowVideoModal] = useState<boolean>(false);

	// Css for navbar css border
	const [scrolled, setScrolled] = useState<boolean>(false);

	useEffect(() => {
		const handleScroll = () => {
			if (window.scrollY >= window.innerHeight) setScrolled(true);
			else setScrolled(false);
		};

		window.addEventListener('scroll', handleScroll);
		return () => window.removeEventListener('scroll', handleScroll);
	}, []);

	const toggleMenu = () => {
		setMenuOpen((prev) => !prev);
	};

	const toggleNavigateIfLogged = (destinationPath: string) => {
		if (authCtx.token && authCtx.isLoggedIn) {
			navigate(destinationPath);
			setMenuOpen(false);
		} else {
			setShowPopup(true);
		}
	};

	const handlePopupLogin = () => {
		setShowPopup(false);
		navigate('/login');
	};

	const handlePopupClose = () => {
		setShowPopup(false);
	};

	const toggleAccess = () => {
		if (authCtx.isLoggedIn) {
			authCtx.logout();
			window.location.reload();
		} else navigate('/login');
	};

	useEffect(() => {
		const handleClickOutsideMobile = (event: MouseEvent) => {
			if (menuOpen && navRef.current && !navRef.current.contains(event.target as Node)) {
				setMenuOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutsideMobile);
		return () => {
			// Delete a potential previous listener
			document.removeEventListener('mousedown', handleClickOutsideMobile);
		};
	}, [menuOpen]);

	const changeLanguage = () => {
		const newLang = currentLanguage === 'it' ? 'en' : 'it';
		i18n.changeLanguage(newLang);
		setCurrentLanguage(newLang);
	};

	return (
		<header
			className={`${props.className} ${styles.nav} ${menuOpen ? styles.nav_open : ''} ${scrolled ? styles.nav_open : ''}`}
			ref={navRef}
		>
			<Logo className={styles.logo} onClick={() => navigate('/home')} />

			<div className={styles.mobile_actions}>
				{/* Campanella */}
				{isRoleAdmin || (
					<div className={styles.bell_container} onClick={() => toggleNavigateIfLogged('/profile#notifications')}>
						<ReportSVG className={styles.alarmIcon} />
					</div>
				)}

				<img
					className={`${menuOpen ? styles.open : ''} ${styles.menu_icon}`}
					src={menuOpen ? MenuOpenSVG : MenuCloseSVG}
					loading="lazy"
					alt="Menu.svg"
					onClick={toggleMenu}
				/>

				<nav className={`${styles.nav_menu} ${menuOpen ? styles.show_menu : ''}`}>
					<ul className={styles.nav_list}>
						<li
							className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`}
							onClick={() => {
								navigate('/home');
								setMenuOpen(false);
							}}
						>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>{t('navbar.home')}</span>
						</li>

						<li
							className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`}
							onClick={() => toggleNavigateIfLogged('/catalog')}
						>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>{t('navbar.catalog')}</span>
						</li>

						{isRoleAdmin && (
							<li
								className={styles.nav_item}
								onClick={() => {
									navigate('/admin-area');
									setMenuOpen(false);
								}}
							>
								{menuOpen && <NavArrow className={styles.arrow} />}
								<span className={styles.nav_text}>{t('navbar.admin')}</span>
							</li>
						)}

						{isRoleAdmin || (
							<li className={styles.nav_item} onClick={() => toggleNavigateIfLogged('/profile')}>
								{menuOpen && <NavArrow className={styles.arrow} />}
								<span className={styles.nav_text}>{t('navbar.userArea')}</span>
							</li>
						)}

						<li
							className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`}
							onClick={() => {
								navigate('/frequently-asked-questions');
								setMenuOpen(false);
							}}
						>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>{t('navbar.faq')}</span>
						</li>

						<li
							className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`}
							onClick={() => setShowVideoModal(true)}
						>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>Tutorial</span>
						</li>

						<li
							className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`}
							onClick={() => toggleAccess()}
						>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>{authCtx.isLoggedIn ? t('navbar.logout') : t('navbar.login')}</span>
						</li>

						<li className={`${styles.nav_item} ${menuOpen ? styles.nav_item_with_arrows : ''}`} onClick={changeLanguage}>
							{menuOpen && <NavArrow className={styles.arrow} />}
							<span className={styles.nav_text}>
								{currentLanguage === 'en' ? (
									<>
										<EnglishSVG className={styles.language_icon} />
										English
									</>
								) : (
									<>
										<ItalianSVG className={styles.language_icon} />
										Italiano
									</>
								)}
							</span>
						</li>
					</ul>
				</nav>
			</div>

			<ConfirmationDialog
				isOpen={showPopup}
				title={t('popup.loginTitle')}
				message={t('popup.messageLogin')}
				confirmText={t('popup.login')}
				cancelText={t('common.cancel')}
				onConfirm={handlePopupLogin}
				onCancel={handlePopupClose}
				showFooterText={true}
				footerText={t('popup.signupLink')}
			/>

			<VideoModal show={showVideoModal} onClose={() => setShowVideoModal(false)} videoId="XeEH6ynr4L4" />
		</header>
	);
};

export default Navbar;
